import React from 'react';

import { formatDate, formatMoney, toMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import { track } from '@betterment/js-runtime';
import { Box, H1, HStack, Icon, Separator, Text } from '@betterment/samba-react';
import { token } from '@betterment/samba-styled-system/tokens';
import { SambaAutoLink } from '@betterment/shared-components';
import { StackedProgressBar } from '@betterment/style-closet-react';

import CardShimmerLoader from './cardShimmerLoader';
import { progressBarData } from './utils';

import { LegacyCard } from '../../Shared/components-v2/legacy-card/LegacyCard';
import { urlBuilder } from '../../Shared/urlBuilder';
import { IraContributionMatchGrantsDocument } from '../graphql/query.iraContributionMatchGrants.generated';

const EARNED_MATCH_AMOUNT_COLOR = 'dataviz.teal.1';
const PROJECTED_MATCH_AMOUNT_COLOR = 'dataviz.teal.3';

type ProjectedMatchAmountTextProps = {
  projectedMatchAmountCents: number;
};

export const ProjectedMatchAmountText: React.FC<ProjectedMatchAmountTextProps> = ({ projectedMatchAmountCents }) => {
  if (projectedMatchAmountCents > 0) {
    return (
      <Text variant="text50" color="text.weak" as="p">
        Based on upcoming recurring deposits
      </Text>
    );
  }

  return (
    <Text variant="text50" color="text.weak" as="p">
      <SambaAutoLink
        to={urlBuilder.appDepositSetup.absolute()}
        onClick={() => {
          track('ElementClicked', {
            name: 'RecurringTransactionPrompt',
            module: 'IraMatchDetails',
            flow: 'IraDetailsApp',
          });
        }}
      >
        Set up recurring transaction
      </SambaAutoLink>
    </Text>
  );
};

const IraMatchOfferCard: React.FC<React.PropsWithChildren> = () => {
  const { data, loading: isLoading } = useGraphQLQuery(IraContributionMatchGrantsDocument);

  if (!data) {
    return null;
  }

  const grant = data.iraContributionMatchGrants[0];

  if (!grant) {
    return null;
  }

  const { contributionWindow, disclosureUrl, projectedMatchAmountV3, matchEligibleAmount, earnedMatchAmount } = grant;

  const projectedMatchAmountCents = projectedMatchAmountV3.cents;
  const matchEligibleAmountCents = matchEligibleAmount?.cents;
  const earnedMatchAmountCents = earnedMatchAmount.cents;

  return (
    <Box css={{ pt: 'space.200' }}>
      {isLoading && <CardShimmerLoader />}
      {!isLoading && (
        <LegacyCard>
          <LegacyCard.Header>
            <H1 variant="title200">Your Betterment match</H1>
          </LegacyCard.Header>
          <LegacyCard.Separator css={{ fullWidthWithInset: 'space.300' }} />
          <LegacyCard.Content>
            <Box>
              <Text>{`Earn ${
                grant.match
              }% on your net Roth and Traditional IRA contributions made at Betterment now through ${formatDate(
                contributionWindow.end,
                'longOrdinal'
              )}.`}</Text>
            </Box>
            <Box css={{ pt: 'space.200' }}>
              <Text variant="text50" css={{ color: 'text.weak' }}>
                Your match is at the discretion of Betterment and is subject to change.{' '}
                <SambaAutoLink to={disclosureUrl} target="_blank">
                  See terms
                </SambaAutoLink>
              </Text>
            </Box>
            {matchEligibleAmountCents && (
              <Box css={{ pt: 'space.300' }}>
                <StackedProgressBar
                  percents={progressBarData({
                    matchEligibleAmountCents,
                    projectedMatchAmountCents,
                    earnedMatchAmountCents,
                    projectedMatchAmountColor: token.var(`colors.${PROJECTED_MATCH_AMOUNT_COLOR}`),
                    earnedMatchAmountColor: token.var(`colors.${EARNED_MATCH_AMOUNT_COLOR}`),
                  })}
                />
                <HStack css={{ justifyContent: 'space-between' }}>
                  <HStack css={{ marginTop: 'space.50' }}>
                    <Text variant="text50" color="text.weak">
                      {formatMoney(toMoney(0), 'defaultDollar')}
                    </Text>
                  </HStack>
                  <HStack css={{ marginTop: 'space.50' }}>
                    <HStack>
                      <Text variant="text50" color="text.weak">
                        Eligible: {formatMoney(toMoney(matchEligibleAmountCents), 'default')}
                      </Text>
                    </HStack>
                  </HStack>
                </HStack>
              </Box>
            )}
            <Box>
              <HStack css={{ justifyContent: 'space-between', marginY: 'space.200', alignItems: 'center' }}>
                <HStack css={{ alignItems: 'center' }}>
                  <HStack>
                    <Icon name="Bullet" size="small" color={EARNED_MATCH_AMOUNT_COLOR} />
                  </HStack>
                  <Box css={{ pr: 'space.150' }}>
                    <Text variant="text100">Match earned</Text>
                  </Box>
                </HStack>
                <Text>{formatMoney(toMoney(earnedMatchAmountCents))}</Text>
              </HStack>
              <Separator />
            </Box>
            <Box>
              <HStack css={{ justifyContent: 'space-between', marginY: 'space.200', alignItems: 'center' }}>
                <HStack css={{ alignItems: 'center' }}>
                  <Box>
                    <Icon name="Bullet" size="small" color={PROJECTED_MATCH_AMOUNT_COLOR} />
                  </Box>
                  <Box css={{ pr: 'space.150' }}>
                    <Text variant="text100">Projected match</Text>
                    <ProjectedMatchAmountText projectedMatchAmountCents={projectedMatchAmountCents} />
                  </Box>
                </HStack>
                <Text>{formatMoney(toMoney(projectedMatchAmountCents))}</Text>
              </HStack>
            </Box>
          </LegacyCard.Content>
        </LegacyCard>
      )}
    </Box>
  );
};

export default IraMatchOfferCard;
